<template>
  <div>
    <!-- Coupon Alert Modal -->
    <modal
      name="coupon-msg-modal"
      :width="350"
      height="auto"
      :adaptive="true"
      classes="v--modal custom-coupon-modal"
    >
      <div style="padding: 24px; text-align: center;">
        <i v-if="!couponError" class="fa-solid fa-circle-check" style="font-size: 3rem; color: #16A34A; margin-bottom: 16px;"></i>
        <i v-if="couponError" class="fa-solid fa-circle-exclamation" style="font-size: 3rem; color: #DC2626; margin-bottom: 16px;"></i>
        <h3 style="font-size: 1.2rem; font-weight: 700; margin-bottom: 12px; color: #111827;">
          {{ couponError ? ($func.parseIso('error') || 'Error') : ($func.parseIso('success') || 'Success') }}
        </h3>
        <p style="font-size: 0.95rem; color: #4B5563; margin-bottom: 24px;">{{ couponMessage }}</p>
        <button class="pg-promo-btn" style="width: 100%; padding: 14px; display: block;" @click="$modal.hide('coupon-msg-modal')">
          {{ $func.parseIso('btnOk') || 'OK' }}
        </button>
      </div>
    </modal>

    <!-- Offers Selection Modal -->
    <modal
      name="offers-modal"
      :width="400"
      height="auto"
      :scrollable="true"
      classes="v--modal offers-modal"
    >
      <div class="offers-modal-head">
        <span class="offers-modal-title">{{ $func.parseIso('selectAnOffer') || 'Select a travel voucher' }}</span>
        <button class="offers-modal-close" type="button" @click="$modal.hide('offers-modal')">
          <i class="fa-regular fa-xmark"></i>
        </button>
      </div>
      <div class="offers-modal-body">
        <ul class="pg-offers-list">
          <li
            v-for="(v, i) in travelVouchers"
            :key="i"
            class="pg-offer-pill"
            :class="{ 'is-selected': isVoucherSelected(v), 'is-applied': isVoucherApplied(v) }"
          >
            <span class="pg-offer-tag"><i class="fa-solid fa-tag"></i></span>
            <span class="pg-offer-code">-${{ Number(v.MontoDescuento || 0).toFixed(2) }}</span>
            <span v-if="isVoucherApplied(v)" class="pg-offer-applied-tag">
              <i class="fa-solid fa-check"></i> {{ $func.parseIso('offerApplied') || 'Aplicado' }}
            </span>
            <button v-else class="pg-offer-select" type="button" @click="selectVoucher(v)">
              <template v-if="isVoucherSelected(v)"><i class="fa-solid fa-check"></i> {{ $func.parseIso('selectedOffer') || 'Selected' }}</template>
              <template v-else>{{ $func.parseIso('selectOffer') || 'Select' }}</template>
            </button>
          </li>
        </ul>
      </div>
    </modal>

    <!-- Confirm Switch Modal (points vs coupon) -->
    <modal
      name="confirm-switch-modal"
      :width="380"
      height="auto"
      :adaptive="true"
      classes="v--modal confirm-switch-modal"
    >
      <div style="padding: 24px; text-align: center;">
        <i class="fa-solid fa-circle-exclamation" style="font-size: 2.5rem; color: #F59E0B; margin-bottom: 16px;"></i>
        <h3 style="font-size: 1.15rem; font-weight: 700; margin-bottom: 12px; color: #111827;">
          {{ confirmSwitch.title || ($func.parseIso('confirmSwitch') || 'Confirmar cambio') }}
        </h3>
        <p style="font-size: 0.95rem; color: #4B5563; margin-bottom: 24px;">{{ confirmSwitch.message }}</p>
        <div style="display: flex; gap: 12px;">
          <button class="pg-confirm-switch-cancel" type="button" @click="confirmSwitchCancel">
            {{ $func.parseIso('cancel') || 'Cancelar' }}
          </button>
          <button class="pg-confirm-switch-ok" type="button" @click="confirmSwitchOk">
            {{ $func.parseIso('next') || 'Confirmar' }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Success state -->
    <div v-if="paid" class="payment-gui-wrapper">
      <div class="pg-success-box">
        <i class="fa-solid fa-circle-check pg-success-icon"></i>
        <h2 class="pg-success-title">{{ $func.parseIso('successfulPayment') || 'Pago procesado con éxito' }}</h2>
        <p class="pg-success-text">{{ $func.parseIso('sentByEmail') || 'Te hemos enviado todos los detalles a tu correo electrónico' }}</p>

        <div v-if="numberRef" class="pg-success-ref">
          <span class="pg-ref-label">{{ $func.parseIso('reservationRef') || 'Referencia de reservación' }}</span>
          <span class="pg-ref-value">{{ numberRef }}</span>
        </div>

        <div class="pg-success-summary" v-if="selectedUnit">
          <div class="pg-summary-head">
            <div class="pg-summary-resort">{{ summaryResortName }}</div>
            <div class="pg-summary-addr">{{ summaryAddress }}</div>
          </div>
          <div class="pg-summary-body">
            <div class="pg-sum-row">
              <span class="pg-sum-label">{{ $func.parseIso('checkInLabel') }}</span>
              <span class="pg-sum-val">{{ formatDate(summaryCheckIn) }}</span>
            </div>
            <div class="pg-sum-row">
              <span class="pg-sum-label">{{ $func.parseIso('checkOutLabel') }}</span>
              <span class="pg-sum-val">{{ formatDate(summaryCheckOut) }}</span>
            </div>
            <div class="pg-sum-row">
              <span class="pg-sum-label">{{ $func.parseIso('duration') }}</span>
              <span class="pg-sum-val">{{ summaryNights }} {{ $func.parseIso('nights') }}</span>
            </div>
            <div class="pg-sum-row">
              <span class="pg-sum-label">{{ $func.parseIso('unit') }}</span>
              <span class="pg-sum-val">{{ summaryUnitType }}</span>
            </div>
            <div class="pg-sum-row">
              <span class="pg-sum-label">{{ $func.parseIso('weekType') }}</span>
              <span class="pg-sum-val">{{ summaryWeekType }}</span>
            </div>
            <div class="pg-sum-row">
              <span class="pg-sum-label">{{ $func.parseIso('kitchen') }}</span>
              <span class="pg-sum-val">{{ summaryKitchen }}</span>
            </div>
            <div class="pg-sum-row">
              <span class="pg-sum-label">{{ $func.parseIso('maxGuests') }}</span>
              <span class="pg-sum-val">{{ summaryMaxOccupancy }} {{ $func.parseIso('guests') }}</span>
            </div>

            <template v-if="traveler">
              <div class="pg-sum-title">
                <i class="fa-regular fa-user"></i>
                {{ $func.parseIso('primaryContact') }}
              </div>
              <div class="pg-sum-row" v-if="summaryTravelerName">
                <span class="pg-sum-label">{{ $func.parseIso('name') }}</span>
                <span class="pg-sum-val">{{ summaryTravelerName }}</span>
              </div>
              <div class="pg-sum-row" v-if="traveler.email">
                <span class="pg-sum-label">{{ $func.parseIso('email') }}</span>
                <span class="pg-sum-val">{{ traveler.email }}</span>
              </div>
              <div class="pg-sum-row" v-if="traveler.phone">
                <span class="pg-sum-label">{{ $func.parseIso('cellphone') }}</span>
                <span class="pg-sum-val">{{ traveler.phone }}</span>
              </div>
              <div class="pg-sum-row" v-if="summaryTravelerAddress">
                <span class="pg-sum-label">{{ $func.parseIso('address') }}</span>
                <span class="pg-sum-val">{{ summaryTravelerAddress }}</span>
              </div>
            </template>

            <template v-if="descuentoData">
              <div class="pg-sum-divider"></div>
              <div class="pg-sum-row" v-if="descuentoAplicadoPreviamente">
                <span class="pg-sum-label">{{ $func.parseIso('total') }}</span>
                <span class="pg-sum-val">${{ originalPrice }}</span>
              </div>
              <div class="pg-sum-row" v-if="descuentoAplicadoPreviamente">
                <span class="pg-sum-label">{{ $func.parseIso('yaCubiertoPorDescuentos') || 'Ya cubierto por descuentos previos' }}</span>
                <span class="pg-sum-val pg-sum-val--discount">-${{ descuentoData.descuentoAplicado }}</span>
              </div>
              <div class="pg-sum-row" v-else>
                <span class="pg-sum-label">{{ $func.parseIso('total') }}</span>
                <span class="pg-sum-val">${{ descuentoData.precioBase }}</span>
              </div>
              <div class="pg-sum-row" v-if="descuentoData.descuentoCupon > 0">
                <span class="pg-sum-label">
                  {{ $func.parseIso('couponDiscount') || 'Cupón' }}
                  <span v-if="descuentoData.cuponAplicado && descuentoData.cuponAplicado.codigo" class="pg-sum-cupon">({{ descuentoData.cuponAplicado.codigo }})</span>
                </span>
                <span class="pg-sum-val pg-sum-val--discount">-${{ descuentoData.descuentoCupon }}</span>
              </div>
              <div class="pg-sum-row" v-if="descuentoData.descuentoPuntos > 0">
                <span class="pg-sum-label">
                  {{ $func.parseIso('pointsDiscount') || 'Puntos' }}
                  <span class="pg-sum-cupon">({{ descuentoData.puntosUsados }} pts)</span>
                </span>
                <span class="pg-sum-val pg-sum-val--discount">-${{ descuentoData.descuentoPuntos }}</span>
              </div>
              <div class="pg-sum-row pg-sum-row--final">
                <span class="pg-sum-label">{{ $func.parseIso('totalToPay') || 'Total a pagar' }}</span>
                <span class="pg-sum-val pg-sum-val--final">${{ descuentoData.precioFinal }}</span>
              </div>
            </template>
            <template v-else>
              <div class="pg-sum-divider"></div>
              <div class="pg-sum-row pg-sum-row--final">
                <span class="pg-sum-label">{{ $func.parseIso('totalToPay') || 'Total a pagar' }}</span>
                <span class="pg-sum-val pg-sum-val--final">${{ finalPrice }}</span>
              </div>
            </template>
          </div>
        </div>

        <button class="pg-btn-new-search" @click="newSearch">
          {{ $func.parseIso('newSearch') || 'Nueva búsqueda' }}
        </button>
      </div>
    </div>

    <div v-else class="payment-gui-wrapper">
      <h1 class="pg-page-title">{{ $func.parseIso('securePayment') || 'Secure Payment' }}</h1>

      <!-- Warning Banner -->
      <div class="pg-warning-banner mb-30">
        <i class="fa-solid fa-lock mr-10" style="color:#9A3412; font-size: 0.9em;"></i>
        <span>{{ $func.parseIso('msgConfirmPayment') || 'Please complete your payment. You have 30 minutes to complete the payment.' }}</span>
      </div>

      <!-- Offers & Loyalty -->
      <div class="pg-offers-box mb-30">
        <h3 class="pg-heading">{{ $func.parseIso('offersLoyalty') || 'TRAVEL VOUCHERS & LOYALTY' }}</h3>
        <div class="pg-row-cols">

          <!-- Available Points -->
          <div class="pg-col flex-1">
            <div class="pg-loyalty-card pg-points-card">
              <div v-if="descuentoLoading" class="pg-loading-overlay"><div class="spinner"></div></div>
              <div class="pg-points-head">
                <h3 class="pg-card-title">{{ $func.parseIso('availablePoints') || 'Available Points' }}</h3>
              </div>
              <div class="pg-points-value">{{ formattedPoints }}</div>
              <div class="pg-points-text">{{ $func.parseIso('pointsValue') || 'Value:' }} ${{ formattedDolares }}</div>
              <div class="pg-points-input-row" v-if="pointsAvailable">
                <input
                  type="number"
                  class="pg-points-input"
                  v-model="puntosInput"
                  min="0"
                  :max="loyalty.Puntos"
                  :placeholder="$func.parseIso('pointsToUse') || 'Puntos a usar'"
                />
                <button class="pg-points-apply-btn" type="button" :disabled="descuentoLoading" @click="applyPointsBtn">
                  {{ $func.parseIso('btnApply') || 'Apply' }}
                </button>
                <button class="pg-points-max-btn" type="button" :disabled="descuentoLoading" @click="applyMaxPointsBtn">
                  {{ $func.parseIso('max') || 'MAX' }}
                </button>
                <button class="pg-points-clear-btn" type="button" :disabled="descuentoLoading" @click="clearPointsBtn" v-if="puntosToUse > 0">
                  <i class="fa-regular fa-xmark"></i>
                </button>
              </div>
              <div v-if="puntosToUse > 0" class="pg-points-applied">
                <i class="fa-solid fa-circle-check"></i> {{ puntosToUse }} {{ $func.parseIso('pointsApplied') || 'puntos aplicados' }}
              </div>
            </div>
          </div>

          <!-- Promo Codes & Available Offers -->
          <div class="pg-col flex-1">
            <div class="pg-loyalty-card pg-promo-card">
              <div v-if="descuentoLoading" class="pg-loading-overlay"><div class="spinner"></div></div>
              <h3 class="pg-card-title">{{ $func.parseIso('promoCodes') || 'Promo Codes' }}</h3>
              <div class="pg-promo-wrapper" :class="{ 'is-disabled': !!selectedVoucher }">
                <input
                  id="j_id_2jk:promoCode"
                  name="j_id_2jk:promoCode"
                  type="text"
                  class="pg-promo-input"
                  v-model="couponCode"
                  :disabled="!!selectedVoucher"
                  :placeholder="$func.parseIso('enterCode') || 'Enter code'"
                />
                <button class="pg-promo-btn" type="button" :disabled="!!selectedVoucher || descuentoLoading" @click="applyCoupon">
                  {{ $func.parseIso('btnApply') || 'Apply' }}
                </button>
              </div>
              <p v-if="selectedVoucher" class="pg-promo-disabled-hint">
                <i class="fa-solid fa-circle-info"></i> {{ $func.parseIso('offerSelectedDisableCoupon') || 'You have a selected travel voucher' }}
              </p>

              <div v-if="appliedCoupon && !selectedVoucher" class="pg-coupon-applied-row">
                <span class="pg-offer-tag"><i class="fa-solid fa-tag"></i></span>
                <span class="pg-coupon-applied-code">{{ appliedCoupon }}</span>
                <button class="pg-offer-remove" type="button" @click="clearAppliedCoupon">
                  <i class="fa-regular fa-xmark"></i> {{ $func.parseIso('removeOffer') || 'Remove' }}
                </button>
              </div>

              <template v-if="isVipWeek">
                <h4 class="pg-offers-title">{{ $func.parseIso('availableOffers') || 'Available Travel Vouchers' }}</h4>
                <div v-if="loyaltyLoading" class="pg-offers-loading">
                  <div class="spinner"></div>
                </div>
                <div v-else-if="travelVouchers.length" class="pg-offers-summary">
                  <div v-if="selectedVoucher" class="pg-offers-selected">
                    <span class="pg-offer-tag"><i class="fa-solid fa-tag"></i></span>
                    <span class="pg-offer-code">-${{ Number(selectedVoucher.MontoDescuento || 0).toFixed(2) }}</span>
                    <button class="pg-offer-change" type="button" @click="openOffersModal">
                      <i class="fa-regular fa-arrows-rotate"></i> {{ $func.parseIso('changeOffer') || 'Change' }}
                    </button>
                    <button class="pg-offer-remove" type="button" @click="clearVoucher">
                      <i class="fa-regular fa-xmark"></i> {{ $func.parseIso('removeOffer') || 'Remove' }}
                    </button>
                  </div>
                  <div v-else class="pg-offers-pick">
                    <span class="pg-offers-count">{{ travelVouchers.length }} {{ $func.parseIso('offersAvailableShort') || 'travel vouchers available' }}</span>
                    <button class="pg-offers-view-btn" type="button" @click="openOffersModal">
                      {{ $func.parseIso('viewOffers') || 'View travel vouchers' }} <i class="fa-regular fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
                <p v-else class="pg-offers-empty">{{ $func.parseIso('noOffersAvailable') || 'You have no travel vouchers available' }}</p>
              </template>
              <p v-else class="pg-offers-empty">
                <i class="fa-solid fa-circle-info"></i> {{ $func.parseIso('vouchersVipOnly') || 'Los travel vouchers solo aplican a semanas VIP Week' }}
              </p>
            </div>
          </div>

        </div>
      </div>

      <!-- Verification Error Banner -->
      <div v-if="verifyError" class="pg-verify-error-banner mb-30">
        <i class="fa-solid fa-circle-exclamation"></i>
        <span>{{ verifyError }}</span>
      </div>

      <!-- Price summary after discounts -->
      <div v-if="descuentoData" class="pg-price-summary mb-30">
        <div v-if="descuentoLoading" class="pg-loading-overlay"><div class="spinner"></div></div>
        <div class="pg-price-summary-row" v-if="descuentoAplicadoPreviamente">
          <span>{{ $func.parseIso('total') || 'Total' }}</span>
          <span>${{ originalPrice }}</span>
        </div>
        <div class="pg-price-summary-row" v-if="descuentoAplicadoPreviamente">
          <span>{{ $func.parseIso('yaCubiertoPorDescuentos') || 'Ya cubierto por descuentos previos' }}</span>
          <span class="pg-price-discount">-${{ descuentoData.descuentoAplicado }}</span>
        </div>
        <div class="pg-price-summary-row" v-else>
          <span>{{ $func.parseIso('total') || 'Total' }}</span>
          <span>${{ descuentoData.precioBase }}</span>
        </div>
        <div class="pg-price-summary-row" v-if="descuentoData.descuentoCupon > 0">
          <span>{{ $func.parseIso('couponDiscount') || 'Cupón' }}<span v-if="descuentoData.cuponAplicado && descuentoData.cuponAplicado.codigo"> ({{ descuentoData.cuponAplicado.codigo }})</span></span>
          <span class="pg-price-discount">-${{ descuentoData.descuentoCupon }}</span>
        </div>
        <div class="pg-price-summary-row" v-if="descuentoData.descuentoPuntos > 0">
          <span>{{ $func.parseIso('pointsDiscount') || 'Puntos' }} ({{ descuentoData.puntosUsados }} pts)</span>
          <span class="pg-price-discount">-${{ descuentoData.descuentoPuntos }}</span>
        </div>
        <div class="pg-price-summary-row pg-price-summary-final">
          <span>{{ $func.parseIso('totalToPay') || 'Total a pagar' }}</span>
          <span class="pg-price-final">${{ finalPrice }}</span>
        </div>
      </div>

      <!-- When price is 0: show Reservar Ahora button, hide Stripe -->
      <div v-if="finalPriceIsZero" class="pg-reservar-ahora-wrap mb-30">
        <button
          class="pg-reservar-ahora-btn"
          :disabled="reserving"
          @click="reservarAhora"
        >
          <div class="spinner" v-if="reserving"></div>
          <template v-else>
            <i class="fa-solid fa-circle-check mr-10"></i>
            {{ $func.parseIso('bookNow') || 'Reservar Ahora' }}
          </template>
        </button>
      </div>

      <!-- When price > 0: show Stripe payment processor -->
      <template v-else>
        <div class="pg-processor-line">
          <span class="pg-pp-rule"></span>
          <span class="pg-pp-label">{{ $func.parseIso('securelyProcessedBy') || 'Securely processed by' }}</span>
          <img src="@/assets/stripe_logo.png" alt="Stripe" class="pg-stripe-logo-img" />
          <span class="pg-pp-rule"></span>
        </div>

        <!-- Stripe Elements (inline, replaces modal checkout) -->
        <h3 class="pg-heading">{{ $func.parseIso('cardDetails') || 'CARD DETAILS' }}</h3>
        <div v-if="clientSecret" class="pg-stripe-elements-wrapper">
          <StripeElements
            ref="stripeEl"
            :key="clientSecret"
            :clientSecret="clientSecret"
            :price="finalPrice"
            :numberRef="numberRef"
            :paying="paying"
            @success="onPaid"
            @pay-clicked="verifyBeforePay"
          />
        </div>
        <div v-else class="pg-loading-wrapper">
          <div class="spinner"></div>
        </div>
      </template>

      <!-- Cancel button -->
      <div class="pg-buttons-footer">
        <a class="pg-btn-cancel" @click="goCancel">
          <i class="fa-solid fa-arrow-left mr-10" style="font-size: 0.85em;"></i> {{ $func.parseIso('cancelTransaction') || 'Cancel Transaction' }}
        </a>
      </div>

      </div>
    </div>
  </div>
</template>

<script>
import StripeElements from "../components/StripeElements.vue";
import { getTokenTC, getMemberObject } from "../utils/sesions";
export default {
  props: {
    week: { type: Object, default: () => ({}) },
    numberRef: { type: String, default: null },
    price: { type: [Number, String], default: null },
    iso: { type: String, default: "es" },
    selectedUnit: { type: Object, default: null },
    traveler: { type: Object, default: null },
  },
  data() {
    return {
      couponCode: "",
      couponMessage: "",
      couponError: false,
      appliedCoupon: null,
      clientSecret: null,
      paid: false,
      puntosToUse: 0,
      puntosInput: "",
      selectedVoucher: null,
      loyalty: { Puntos: 0, Dolares: 0, Travelvouchers: [] },
      loyaltyLoading: false,
      descuentoData: null,
      reserving: false,
      verifyError: "",
      descuentoLoading: false,
      paying: false,
      confirmSwitch: {
        show: false,
        title: "",
        message: "",
        pendingAction: null,
      },
    };
  },
  computed: {
    originalPrice() {
      return this.price;
    },
    summaryUnitType() {
      return this.selectedUnit ? this.selectedUnit.UnitType : "";
    },
    summaryWeekType() {
      return this.selectedUnit ? this.selectedUnit.InvTypeInDetail : "";
    },
    isVipWeek() {
      return String(this.selectedUnit?.InvTypeInDetail || "")
        .toLowerCase()
        .includes("vip");
    },
    summaryKitchen() {
      return this.selectedUnit ? this.selectedUnit.KitchenType : "";
    },
    summaryMaxOccupancy() {
      return this.selectedUnit ? this.selectedUnit.MaxOccupancy : "";
    },
    summaryCheckIn() {
      return this.selectedUnit ? this.selectedUnit.CheckInDate : "";
    },
    summaryCheckOut() {
      return this.selectedUnit ? this.selectedUnit.CheckOutDate : "";
    },
    summaryNights() {
      return this.selectedUnit ? this.selectedUnit.Nights : "";
    },
    summaryCost() {
      return this.selectedUnit ? this.selectedUnit.Cost : 0;
    },
    summaryPerNight() {
      if (!this.selectedUnit) return 0;
      const nights = parseFloat(this.selectedUnit.Nights) || 1;
      const cost = parseFloat(this.selectedUnit.Cost) || 0;
      return Math.round((cost / nights) * 100) / 100;
    },
    summaryResortName() {
      return this.week && this.week.ResortName ? this.week.ResortName : "";
    },
    summaryAddress() {
      if (!this.week) return "";
      return [
        this.week.ResortAddress1,
        this.week.ResortAddress2,
        this.week.ResortAddress3,
      ]
        .filter(Boolean)
        .join(" ");
    },
    summaryTravelerName() {
      if (!this.traveler) return "";
      return [this.traveler.name, this.traveler.lastname]
        .filter(Boolean)
        .join(" ");
    },
    summaryTravelerAddress() {
      if (!this.traveler) return "";
      return [
        this.traveler.address,
        this.traveler.city,
        this.traveler.state,
        this.traveler.zipcode,
        this.traveler.country,
      ]
        .filter(Boolean)
        .join(", ");
    },
    pointsAvailable() {
      return (parseFloat(this.loyalty.Dolares) || 0) > 0;
    },
    formattedPoints() {
      const p = parseInt(this.loyalty.Puntos, 10) || 0;
      return p.toLocaleString("en-US");
    },
    formattedDolares() {
      return (parseFloat(this.loyalty.Dolares) || 0).toFixed(2);
    },
    travelVouchers() {
      return Array.isArray(this.loyalty.Travelvouchers)
        ? this.loyalty.Travelvouchers
        : [];
    },
    activeCupon() {
      if (this.selectedVoucher) return this.selectedVoucher.Codigo || null;
      return this.appliedCoupon;
    },
    finalPrice() {
      if (this.descuentoData) {
        return Math.max(0, parseFloat(this.descuentoData.precioFinal) || 0);
      }
      return Math.max(0, parseFloat(this.price) || 0);
    },
    descuentoAplicadoPreviamente() {
      // Descuentos (coupon/points) ya quemados en intentos anteriores de la
      // MISMA reserva: la API ahora los deja en descuentoAplicado y parte del
      // saldo neto; se muestran para que el cobro de tarjeta no parezca total.
      return (
        !!this.descuentoData &&
        typeof this.descuentoData.descuentoAplicado === "number" &&
        this.descuentoData.descuentoAplicado > 0 &&
        !!this.price
      );
    },
    finalPriceIsZero() {
      return this.finalPrice === 0;
    },
    hasPointsOrCoupon() {
      return (this.puntosToUse > 0) || !!this.activeCupon;
    },
  },
  components: {
    StripeElements,
  },
  async created() {
    this.$bus.on("change-iso", () => {
      this.$forceUpdate();
    });
    await this.fetchClientSecret();
    await this.fetchLoyalty();
    await this.calcularDescuento(false, false);
  },
  methods: {
    goCancel() {
      this.$emit("cancel");
    },
    async fetchClientSecret() {
      if (!this.numberRef) return;
      const params = new URLSearchParams();
      params.append("numberRef", this.numberRef);
      const email = getMemberObject()?.Email;
      if (email) params.append("email", email);
      if (this.puntosToUse > 0) params.append("puntos", this.puntosToUse);
      if (this.activeCupon) params.append("cupon", this.activeCupon);
      try {
        const { data } = await this.$axios.get(
          `/payment/create/payment-intent?${params.toString()}`
        );
        this.clientSecret = data.clientSecret;
      } catch (error) {
        const resp = error && error.response && error.response.data;
        // Si la API rechaza crear el intent (saldo 0, descuento ya aplicado,
        // sobre-cobro) NO se debe dejar un clientSecret viejo apuntando al
        // precio completo: se muestra el motivo en pantalla.
        if (
          resp &&
          (resp.error === "already_fully_discounted" ||
            resp.error === "discount_already_applied" ||
            resp.error === "overpayment_detected")
        ) {
          this.verifyError = this.localizeApiError(resp) || resp.message || "";
        }
        console.log("Error creating payment intent", error);
      }
    },
    formatDate(date) {
      return this.$moment(date).format("LL");
    },
    async fetchLoyalty() {
      const token = getTokenTC();
      if (!token) return;
      this.loyaltyLoading = true;
      try {
        const { data } = await this.$axios.get(
          `/puntos/mis-puntos?token=${token}`
        );
        this.loyalty = (data && data.data) || data || {
          Puntos: 0,
          Dolares: 0,
          Travelvouchers: [],
        };
      } catch (error) {
        console.log("Error fetching loyalty", error);
      } finally {
        this.loyaltyLoading = false;
      }
    },
    toggleVoucher(voucher) {
      this.selectedVoucher =
        this.selectedVoucher &&
        this.selectedVoucher.Codigo === voucher.Codigo
          ? null
          : voucher;
    },
    isVoucherSelected(voucher) {
      return !!(
        this.selectedVoucher && this.selectedVoucher.Codigo === voucher.Codigo
      );
    },
    isVoucherApplied(voucher) {
      const codigo = String((voucher && voucher.Codigo) || "").toUpperCase();
      return (
        !!codigo &&
        !!this.appliedCoupon &&
        String(this.appliedCoupon).toUpperCase() === codigo
      );
    },
    clearAppliedCoupon() {
      this.appliedCoupon = null;
      this.couponCode = "";
      this.couponMessage = "";
      this.couponError = false;
      this.calcularDescuento();
    },
    openOffersModal() {
      this.$modal.show("offers-modal");
    },
    selectVoucher(voucher) {
      this.selectedVoucher = voucher;
      this.couponCode = "";
      this.couponMessage = "";
      this.couponError = false;
      this.appliedCoupon = null;
      this.$modal.hide("offers-modal");
      if (this.puntosToUse > 0) {
        this.openConfirmSwitch(
          null,
          (this.$func.parseIso('switchCouponOverPoints') ||
            'Al aplicar un cupón o travel voucher se quitarán los puntos aplicados. ¿Deseas continuar?'),
          'selectVoucher'
        );
        return;
      }
      this._doSelectVoucher();
    },
    _doSelectVoucher() {
      this.calcularDescuento();
    },
    clearVoucher() {
      this.selectedVoucher = null;
      this.calcularDescuento();
    },
    async applyCoupon() {
      if (!this.couponCode) return;
      this.couponMessage = "";
      this.couponError = false;
      this.appliedCoupon = this.couponCode;
      if (this.puntosToUse > 0) {
        this.openConfirmSwitch(
          null,
          (this.$func.parseIso('switchCouponOverPoints') ||
            'Al aplicar un cupón o travel voucher se quitarán los puntos aplicados. ¿Deseas continuar?'),
          'applyCoupon'
        );
        return;
      }
      await this._doApplyCoupon();
    },
    async _doApplyCoupon() {
      const ok = await this.calcularDescuento();
      if (ok) {
        this.couponMessage = this.$func.parseIso('validCoupon') || "Cupón válido";
        this.couponError = false;
        const codigoAplicado =
          this.descuentoData && this.descuentoData.cuponAplicado && this.descuentoData.cuponAplicado.codigo;
        if (codigoAplicado) this.appliedCoupon = codigoAplicado;
      } else {
        const msg =
          this.verifyError ||
          (this.$func.parseIso('couponNotApplicable') || "Cupón inválido o no aplicable");
        this.appliedCoupon = null;
        await this.calcularDescuento(false, false);
        this.couponMessage = msg;
        this.couponError = true;
        this.verifyError = msg;
      }
      this.$modal.show("coupon-msg-modal");
    },
    applyPointsBtn() {
      const val = parseInt(this.puntosInput, 10);
      this.puntosToUse = isNaN(val) || val < 0 ? 0 : val;
      if (this.puntosToUse > 0 && this.activeCupon) {
        const label = this.selectedVoucher
          ? (this.$func.parseIso('travelVoucher') || 'travel voucher')
          : (this.$func.parseIso('coupon') || 'cupón');
        this.openConfirmSwitch(
          null,
          (this.$func.parseIso('switchPointsOverCoupon') ||
            `Al aplicar puntos se quitará el ${label} aplicado. ¿Deseas continuar?`).replace('{label}', label),
          'applyPoints'
        );
        return;
      }
      this.calcularDescuento();
    },
    applyMaxPointsBtn() {
      const max = parseInt(this.loyalty.Puntos, 10) || 0;
      this.puntosToUse = max;
      this.puntosInput = String(max);
      if (this.activeCupon) {
        const label = this.selectedVoucher
          ? (this.$func.parseIso('travelVoucher') || 'travel voucher')
          : (this.$func.parseIso('coupon') || 'cupón');
        this.openConfirmSwitch(
          null,
          (this.$func.parseIso('switchPointsOverCoupon') ||
            `Al aplicar puntos se quitará el ${label} aplicado. ¿Deseas continuar?`).replace('{label}', label),
          'applyMaxPoints'
        );
        return;
      }
      this.calcularDescuento();
    },
    clearPointsBtn() {
      this.puntosToUse = 0;
      this.puntosInput = "";
      this.calcularDescuento();
    },
    clearCuponState() {
      this.selectedVoucher = null;
      this.appliedCoupon = null;
      this.couponCode = "";
      this.couponMessage = "";
      this.couponError = false;
    },
    clearPointsState() {
      this.puntosToUse = 0;
      this.puntosInput = "";
    },
    openConfirmSwitch(title, message, action) {
      this.confirmSwitch.title = title || "";
      this.confirmSwitch.message = message || "";
      this.confirmSwitch.pendingAction = action;
      this.confirmSwitch.show = true;
      this.$modal.show("confirm-switch-modal");
    },
    confirmSwitchOk() {
      const action = this.confirmSwitch.pendingAction;
      this.confirmSwitch.show = false;
      this.confirmSwitch.pendingAction = null;
      this.$modal.hide("confirm-switch-modal");
      if (action === "applyPoints") {
        this.clearCuponState();
        this.calcularDescuento();
      } else if (action === "applyMaxPoints") {
        this.clearCuponState();
        this.calcularDescuento();
      } else if (action === "applyCoupon") {
        this.clearPointsState();
        this._doApplyCoupon();
      } else if (action === "selectVoucher") {
        this.clearPointsState();
        this._doSelectVoucher();
      }
    },
    confirmSwitchCancel() {
      const action = this.confirmSwitch.pendingAction;
      this.confirmSwitch.show = false;
      this.confirmSwitch.pendingAction = null;
      this.$modal.hide("confirm-switch-modal");
      if (action === "applyPoints" || action === "applyMaxPoints") {
        this.puntosToUse = 0;
        this.puntosInput = "";
      } else if (action === "applyCoupon") {
        this.appliedCoupon = null;
        this.couponCode = "";
      } else if (action === "selectVoucher") {
        this.selectedVoucher = null;
      }
    },
    localizeApiError(resp) {
      if (!resp) return "";
      const reason = resp.error ? String(resp.error) : "";
      const reasonKeys = {
        unauthorized: "tokenRequired",
        reserve_not_found: "reserveNotFound",
        member_not_found: "memberNotFound",
        insufficient_balance: "insufficientPoints",
        cupon_vip_week_required: "cuponVipWeekRequired",
        discount_already_applied: "discountAlreadyApplied",
        overpayment_detected: "overpaymentDetected",
        already_fully_discounted: "alreadyFullyDiscounted",
      };
      const key =
        reasonKeys[reason] ||
        (reason.indexOf("cupon_") === 0 ? "couponNotApplicable" : "discountCalcError");
      return this.$func.parseIso(key) || resp.message || "";
    },
    async calcularDescuento(isVerification = false, showLoading = true) {
      if (!this.numberRef) return false;
      const token = getTokenTC();
      if (!token) {
        this.verifyError = this.$func.parseIso('tokenRequired') || "Token requerido. Inicia sesión nuevamente.";
        return false;
      }
      this.verifyError = "";
      if (showLoading) {
        this.descuentoLoading = true;
        this.$emit("descuento-loading", true);
      }
      try {
        const payload = {
          numberRef: this.numberRef,
          puntos: this.puntosToUse || 0,
        };
        if (this.activeCupon) payload.cupon = this.activeCupon;
        const { data } = await this.$axios.post(
          "/puntos/calcular-descuento",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data && data.success && data.data) {
          this.descuentoData = data.data;
          const d = data.data;
          if (d.saldoPuntos != null) {
            const origPuntos = parseInt(this.loyalty.Puntos, 10) || 0;
            const origDolares = parseFloat(this.loyalty.Dolares) || 0;
            this.loyalty.Puntos = d.saldoPuntos;
            this.loyalty.Dolares =
              origPuntos > 0
                ? Math.round((d.saldoPuntos / origPuntos) * origDolares * 100) / 100
                : 0;
          }
          this.puntosToUse = d.puntosUsados || 0;
          this.puntosInput = this.puntosToUse > 0 ? String(this.puntosToUse) : "";
          this.$emit("discount-update", this.descuentoData);
          if (!isVerification) {
            this.fetchClientSecret();
          }
          return true;
        }
        this.verifyError =
          this.localizeApiError(data) ||
          (data && data.message) ||
          (this.$func.parseIso('discountCalcError') || "Error al calcular el descuento");
        return false;
      } catch (error) {
        const resp = error && error.response && error.response.data;
        this.verifyError =
          this.localizeApiError(resp) ||
          (this.$func.parseIso('discountCalcError') || "Error al calcular el descuento");
        return false;
      } finally {
        if (showLoading) {
          this.descuentoLoading = false;
          this.$emit("descuento-loading", false);
        }
      }
    },
    async confirmManualPointsCoupons() {
      if (!this.numberRef) return null;
      const token = getTokenTC();
      if (!token) {
        this.verifyError = this.$func.parseIso('tokenRequired') || "Token requerido. Inicia sesión nuevamente.";
        return null;
      }
      this.verifyError = "";
      try {
        const payload = {
          numberRef: this.numberRef,
          puntos: this.puntosToUse || 0,
        };
        if (this.activeCupon) payload.cupon = this.activeCupon;
        const { data } = await this.$axios.post(
          "/puntos/payment/confirm/manual-points-coupons",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data && data.success) {
          return data.data;
        }
        this.verifyError =
          (data && data.message) ||
          (this.$func.parseIso('manualPaymentError') || "Error al confirmar el pago manual");
        return null;
      } catch (error) {
        const msg =
          (error && error.response && error.response.data && error.response.data.message) ||
          (error && error.response && error.response.data && error.response.data.error) ||
          (this.$func.parseIso('manualPaymentErrorCoupons') ||
            "Error al confirmar el pago manual con puntos y cupones");
        this.verifyError = msg;
        return null;
      }
    },
    async verifyBeforePay() {
      this.verifyError = "";
      this.paying = true;
      try {
        const ok = await this.calcularDescuento(true, false);
        if (!ok) return;
        if (this.hasPointsOrCoupon) {
          const result = await this.confirmManualPointsCoupons();
          if (!result) return;
          if (result.completo) {
            this.onPaid();
            return;
          }
        }
        if (!this.$refs.stripeEl) {
          this.verifyError =
            this.$func.parseIso('stripeInitError') ||
            "No se pudo iniciar el pago con tarjeta. Inténtalo nuevamente.";
          return;
        }
        await this.$refs.stripeEl.confirmPaymentWithStripe();
      } finally {
        this.paying = false;
      }
    },
    async reservarAhora() {
      if (this.reserving) return;
      this.reserving = true;
      this.verifyError = "";
      const ok = await this.calcularDescuento(true, false);
      if (!ok) {
        this.reserving = false;
        return;
      }
      const result = await this.confirmManualPointsCoupons();
      this.reserving = false;
      if (!result) return;
      if (result.completo) {
        this.onPaid();
      } else {
        this.verifyError =
          (this.$func.parseIso('notFullyCovered') || "El descuento no cubre el total.") +
          ` ${this.$func.parseIso('totalToPay') || 'Total a pagar'}: $${result.precioFinal}`;
      }
    },
    onPaid() {
      this.paid = true;
      this.$emit("paid");
    },
    newSearch() {
      this.$emit("new-search");
    },
  },
};
</script>

<style scoped>
.payment-gui-wrapper {
  font-family: 'Ubuntu', sans-serif;
  color: #111827;
}

/* Stripe Elements inline wrapper */
.pg-stripe-elements-wrapper {
  max-width: 100%;
}

.pg-loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

/* Success state */
.pg-success-box {
  text-align: center;
  padding: 48px 24px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid #F3F4F6;
}
.pg-success-icon {
  font-size: 4rem;
  color: #16A34A;
  margin-bottom: 16px;
}
.pg-success-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}
.pg-success-text {
  font-size: 1rem;
  color: #4B5563;
  margin-bottom: 32px;
}
.pg-btn-new-search {
  background: #22C55E;
  color: #ffffff;
  font-weight: 700;
  font-size: 1rem;
  padding: 14px 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  transition: background 0.2s;
}
.pg-btn-new-search:hover {
  background: #16A34A;
}

/* Success reservation reference */
.pg-success-ref {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 28px;
}
.pg-ref-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #16A34A;
}
.pg-ref-value {
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #111827;
}

/* Success purchase summary */
.pg-success-summary {
  text-align: left;
  background: #F8FAFC;
  border: 1px solid #E6E8F0;
  border-radius: 12px;
  margin-bottom: 28px;
  overflow: hidden;
}
.pg-summary-head {
  padding: 16px 20px;
  background: #FFFFFF;
  border-bottom: 1px solid #E6E8F0;
}
.pg-summary-resort {
  font-size: 1.05rem;
  font-weight: 700;
  color: #102a43;
}
.pg-summary-addr {
  font-size: 0.85rem;
  color: #627d98;
  margin-top: 2px;
}
.pg-summary-body {
  padding: 8px 20px 16px;
}
.pg-sum-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  padding: 7px 0;
  border-bottom: 1px dashed #E4E7EB;
}
.pg-sum-row:last-child {
  border-bottom: none;
}
.pg-sum-label {
  color: #627d98;
}
.pg-sum-val {
  color: #243b53;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}
.pg-sum-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #102a43;
  margin: 12px 0 4px;
  padding-top: 10px;
  border-top: 1px dashed #E4E7EB;
}
.pg-sum-title i {
  color: #0472aa;
  font-size: 14px;
}
.pg-sum-cupon {
  font-size: 0.75rem;
  color: #9aa5b1;
  font-weight: 500;
}
.pg-sum-val--discount {
  color: #16a34a;
}
.pg-sum-divider {
  height: 1px;
  background: #E0E6EA;
  margin: 12px 0;
}
.pg-sum-row--final {
  border-top: 1px solid #E0E6EA;
  margin-top: 4px;
  padding-top: 12px;
  font-weight: 700;
  color: #111827;
}
.pg-sum-val--final {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
}

.pg-page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1F2937;
  margin-bottom: 24px;
}
.pg-warning-banner {
  background-color: #FFEDD5;
  color: #9A3412;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 16px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
}

.pg-heading {
  font-size: 0.75rem;
  font-weight: 700;
  color: #1F2937;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

/* Grid layout */
.pg-row-cols {
  display: flex;
  gap: 20px;
  align-items: stretch;
}
.flex-1 {
  flex: 1;
}
.h-100 {
  height: 100%;
}

/* Cards */
.pg-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid #F3F4F6;
  transition: all 0.2s ease;
}
.pg-card:not(.info-only) {
  cursor: pointer;
}
.pg-card.active-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

/* Summary icon */
.pg-summary-icon {
  width: 48px;
  height: 48px;
  background: #F3F4FB;
  color: #4338CA;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-right: 20px;
}

/* Card Texts */
.pg-card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}
.pg-texts {
  display: flex;
  flex-direction: column;
}
.pg-main-text {
  font-weight: 600;
  color: #374151;
  font-size: 1.05rem;
}
.pg-sub-text {
  font-size: 0.85rem;
  color: #6B7280;
  margin-top: 4px;
}
.pg-price-text {
  font-weight: 700;
  color: #111827;
  font-size: 1.35rem;
}

/* Payment Processor Line */
.pg-processor-line {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
}
.pg-pp-rule {
  flex: 1;
  height: 1px;
  background: #E6E8F0;
}
.pg-pp-label {
  font-size: 0.95rem;
  color: #4B5563;
  font-weight: 500;
  white-space: nowrap;
}
.pg-stripe-logo-img {
  height: 20px;
  width: auto;
  object-fit: contain;
}

/* Promo Code */
.pg-promo-wrapper {
  display: flex;
  gap: 16px;
  height: 68px;
}
.pg-promo-input {
  flex: 1;
  background: #F8FAFC;
  border: 1px solid #F1F5F9;
  border-radius: 8px;
  padding: 0 20px;
  font-size: 1rem;
  color: #334155;
  transition: border-color 0.2s;
  outline: none;
}
.pg-promo-input:focus {
  border-color: #CBD5E1;
}
.pg-promo-btn {
  background: #0F172A;
  color: #FFFFFF;
  border: none;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0 48px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}
.pg-promo-btn:hover {
  background: #1E293B;
}

.pg-coupon-msg {
  margin-top: 10px;
  font-size: 0.85rem;
  font-weight: 500;
}
.pg-coupon-success {
  color: #16A34A;
}
.pg-coupon-error {
  color: #DC2626;
}

/* Buttons */
.pg-buttons-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 40px 0;
}
.pg-btn-cancel {
  font-weight: 600;
  color: #4B5563;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
}
.pg-btn-pay {
  background: #22C55E;
  color: #FFFFFF;
  font-weight: 700;
  font-size: 1.1rem;
  padding: 16px 36px;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  display: flex;
  align-items: center;
  transition: background 0.2s;
}
.pg-btn-pay:hover {
  background: #16A34A;
}

/* Inline loading spinner (while clientSecret loads) */
.spinner {
  color: #4338CA;
  font-size: 22px;
  text-indent: -99999px;
  margin: 0 auto;
  position: relative;
  width: 20px;
  height: 20px;
  box-shadow: inset 0 0 0 2px;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  border-radius: 50%;
  animation: pgSpin 1s infinite linear;
}
@keyframes pgSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Modal Fixes */
:deep(.v--modal-overlay .v--modal.custom-coupon-modal) {
  border-radius: 12px !important;
  box-shadow: 0 15px 50px rgba(0,0,0,0.2) !important;
}
:deep(.v--modal-overlay .v--modal.confirm-switch-modal) {
  border-radius: 12px !important;
  box-shadow: 0 15px 50px rgba(0,0,0,0.2) !important;
}
.pg-confirm-switch-ok {
  flex: 1;
  background: #22C55E;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 14px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s;
}
.pg-confirm-switch-ok:hover {
  background: #16A34A;
}
.pg-confirm-switch-cancel {
  flex: 1;
  background: #F3F4F6;
  color: #4B5563;
  border: 1px solid #E6E8F0;
  border-radius: 8px;
  padding: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s;
}
.pg-confirm-switch-cancel:hover {
  background: #E6E8F0;
}

/* Offers & Loyalty */
.pg-offers-box {
  background: #F4F6FB;
  border: 1px solid #E6E8F0;
  border-radius: 12px;
  padding: 20px;
}
.pg-loyalty-card {
  background: #FFFFFF;
  border-radius: 12px;
  border: 1px solid #E6E8F0;
  padding: 20px;
  height: 100%;
  position: relative;
}
.pg-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  z-index: 2;
}
.pg-points-card {
  background: #EBEEF9;
  border-color: #D9DEEE;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.pg-card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}
.pg-points-head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 4px;
}
.pg-points-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #16A34A;
  color: #16A34A;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.pg-points-value {
  font-size: 2.8rem;
  font-weight: 700;
  color: #111827;
  margin: 10px 0 4px;
  line-height: 1.1;
}
.pg-points-text {
  font-size: 1.15rem;
  color: #6B7280;
  margin-bottom: 16px;
}
.pg-points-toggle-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.pg-points-toggle-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1F2937;
}
.pg-points-applied {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  color: #16A34A;
  font-weight: 600;
  font-size: 0.95rem;
}
.pg-points-input-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.pg-points-input {
  flex: 1;
  max-width: 120px;
  background: #F8FAFC;
  border: 1px solid #D9DEEE;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 1rem;
  color: #334155;
  text-align: center;
  font-weight: 700;
  outline: none;
  transition: border-color 0.2s;
}
.pg-points-input:focus {
  border-color: #22C55E;
}
.pg-points-input::-webkit-outer-spin-button,
.pg-points-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.pg-points-input[type=number] {
  -moz-appearance: textfield;
}
.pg-points-apply-btn {
  background: #4338CA;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}
.pg-points-apply-btn:hover {
  background: #3730A3;
}
.pg-points-max-btn {
  background: #22C55E;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}
.pg-points-max-btn:hover {
  background: #16A34A;
}
.pg-points-clear-btn {
  background: #EF4444;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  flex-shrink: 0;
}
.pg-points-clear-btn:hover {
  background: #DC2626;
}

/* Verification error banner */
.pg-verify-error-banner {
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #DC2626;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 16px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.pg-verify-error-banner i {
  font-size: 1.3rem;
  flex-shrink: 0;
}

/* Price summary */
.pg-price-summary {
  background: #F8FAFC;
  border: 1px solid #E6E8F0;
  border-radius: 12px;
  padding: 20px;
  position: relative;
}
.pg-price-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 0.95rem;
  color: #4B5563;
  border-bottom: 1px solid #F1F5F9;
}
.pg-price-summary-row:last-child {
  border-bottom: none;
}
.pg-price-discount {
  color: #16A34A;
  font-weight: 700;
}
.pg-price-summary-final {
  border-top: 2px solid #E6E8F0;
  border-bottom: none;
  margin-top: 4px;
  padding-top: 14px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
}
.pg-price-final {
  font-size: 1.6rem;
  font-weight: 700;
  color: #111827;
}

/* Reservar Ahora button (price = 0) */
.pg-reservar-ahora-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
}
.pg-reservar-ahora-btn {
  background: #22C55E;
  color: #ffffff;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 1.3rem;
  padding: 18px 48px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(34, 197, 94, 0.35);
  transition: background 0.2s, transform 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.pg-reservar-ahora-btn:hover:not(:disabled) {
  background: #16A34A;
  transform: translateY(-1px);
}
.pg-reservar-ahora-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pg-promo-card {
  background: #FBFBFD;
}
.pg-offers-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #374151;
  margin: 20px 0 12px;
}
.pg-offers-loading {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}
.pg-offers-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pg-offer-pill {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #E7EBF9;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: border-color 0.2s;
}
.pg-offer-pill.is-selected {
  border-color: #22C55E;
}
.pg-offer-tag {
  color: #4338CA;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}
.pg-offer-code {
  font-weight: 700;
  color: #111827;
  flex: 1;
  letter-spacing: 0.02em;
}
.pg-offer-select {
  background: none;
  border: none;
  color: #16A34A;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.pg-offer-select:hover {
  text-decoration: underline;
}
.pg-offer-pill.is-applied {
  opacity: 0.55;
}
.pg-offer-pill.is-applied .pg-offer-code {
  text-decoration: line-through;
  color: #6B7280;
}
.pg-offer-applied-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #111827;
  font-size: 0.9rem;
  font-weight: 700;
}
.pg-offer-applied-tag i {
  color: #16A34A;
}
.pg-offers-empty {
  color: #9CA3AF;
  font-size: 0.9rem;
  margin: 8px 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Promo disabled when offer selected */
.pg-promo-wrapper.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}
.pg-promo-disabled-hint {
  margin: 8px 0 0;
  font-size: 0.8rem;
  color: #6B7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Applied coupon chip (manual coupon) */
.pg-coupon-applied-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding: 12px 14px;
  background: #E7EBF9;
  border: 1px solid #22C55E;
  border-radius: 10px;
}
.pg-coupon-applied-code {
  flex: 1;
  font-weight: 700;
  color: #111827;
  letter-spacing: 0.02em;
  word-break: break-word;
}

/* Offers summary (in-card) */
.pg-offers-summary {
  margin-top: 4px;
}
.pg-offers-selected {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #E7EBF9;
  border-radius: 10px;
  border: 1px solid #22C55E;
}
.pg-offers-pick {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #F8FAFC;
  border: 1px solid #E6E8F0;
  border-radius: 10px;
}
.pg-offers-count {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
}
.pg-offers-view-btn {
  background: #4338CA;
  color: #FFFFFF;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
}
.pg-offers-view-btn:hover {
  background: #3730A3;
}
.pg-offer-change {
  background: none;
  border: none;
  color: #4338CA;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  margin-left: auto;
}
.pg-offer-change:hover {
  text-decoration: underline;
}
.pg-offer-remove {
  background: none;
  border: none;
  color: #DC2626;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
}
.pg-offer-remove:hover {
  text-decoration: underline;
}

/* Offers Modal */
.offers-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #E6E8F0;
}
.offers-modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}
.offers-modal-close {
  background: none;
  border: none;
  font-size: 1.1rem;
  color: #6B7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
}
.offers-modal-close:hover {
  color: #111827;
}
.offers-modal-body {
  padding: 16px 20px;
}
:deep(.v--modal-overlay .v--modal.offers-modal) {
  border-radius: 12px !important;
  box-shadow: 0 15px 50px rgba(0,0,0,0.2) !important;
}

@media (max-width: 600px) {
  .pg-row-cols {
    flex-direction: column;
  }
  .pg-col .pg-heading[style*="opacity"] {
    display: none;
  }
  .pg-buttons-footer {
    flex-direction: column-reverse;
    gap: 20px;
  }
  .pg-btn-pay {
    width: 100%;
    justify-content: center;
  }
  .pg-card-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .pg-promo-wrapper {
    flex-direction: column;
  }
  .pg-promo-btn {
    padding: 16px;
  }
  .pg-offers-pick {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .pg-offers-view-btn {
    justify-content: center;
  }
  .pg-offers-selected {
    flex-wrap: wrap;
  }
}
</style>
